import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CopyChecklist from "./CopyChecklist";
import { checklist } from "./checklist";
import { childrenOf, isBlankHtml, loadPageProducts, loadStorefrontPages } from "./storefrontPages";

const WORDS = {
  en: {
    lang: "English",
    all: "All",
    filterBy: "Filter by",
    filters: ["Brands", "Type", "Size", "Price"],
    sort: "Most popular",
    save: "Save",
    soldOut: "Sold out",
    currency: "SAR",
  },
  ar: {
    lang: "العربية",
    all: "الكل",
    filterBy: "تصفية حسب",
    filters: ["الماركات", "النوع", "الحجم", "السعر"],
    sort: "الأكثر شعبية",
    save: "وفّر",
    soldOut: "نفدت الكمية",
    currency: "ر.س",
  },
};

const PLACEHOLDERS = [0, 1, 2, 3, 4, 5].map((id) => ({ id, placeholder: true }));

const money = (amount) => (Number.isInteger(amount) ? String(amount) : amount.toFixed(2));

/**
 * The "Page" tab of the live preview: the page as the storefront prints it.
 * The pills row of the pages under (or beside) this one, the filter column
 * with the written copy in its white box underneath, and the heading with
 * the first real product cards of the listing. Everything live comes from
 * the same public endpoints the storefront reads; a page not yet saved
 * shows the frame with placeholder cards.
 */
export default function PagePreview({ en, ar, path, currentPath }) {
  const { t } = useTranslation();
  const [lang, setLang] = useState("en");
  const [live, setLive] = useState({ pills: [], products: [] });
  const page = lang === "ar" ? ar : en;
  const words = WORDS[lang];
  const rtl = lang === "ar";
  const empty = isBlankHtml(page.html);

  useEffect(() => {
    let alive = true;
    if (!currentPath) {
      setLive({ pills: [], products: [] });
      return undefined;
    }
    (async () => {
      let pills = [];
      let products = [];
      try {
        const pages = await loadStorefrontPages(lang);
        const me = pages.find((entry) => entry.path === currentPath);
        const children = childrenOf(pages, currentPath);
        if (children.length) {
          pills = [{ name: words.all, active: true }, ...children.map((child) => ({ name: child.name }))];
        } else if (me?.parent) {
          pills = [
            { name: words.all },
            ...childrenOf(pages, me.parent).map((page) => ({ name: page.name, active: page.path === currentPath })),
          ];
        }
        if (me) products = await loadPageProducts(lang, me.filters, 6);
      } catch {
        // The frame stands without the live parts.
      }
      if (alive) setLive({ pills, products });
    })();
    return () => {
      alive = false;
    };
  }, [lang, currentPath, words.all]);

  const cards = live.products.length ? live.products : PLACEHOLDERS;

  return (
    <div className="lp-page">
      <div className="lp-page__langs" role="tablist" aria-label={t("Language")}>
        {["en", "ar"].map((code) => (
          <button
            key={code}
            type="button"
            role="tab"
            aria-selected={lang === code}
            className={`lp-page__lang${lang === code ? " is-active" : ""}`}
            onClick={() => setLang(code)}
          >
            {WORDS[code].lang}
          </button>
        ))}
      </div>

      <div className="lp-page__frame" dir={rtl ? "rtl" : "ltr"}>
        <div className="lp-page__chrome" dir="ltr">
          <span /> <span /> <span />
          <em>nysmah.com{path.replace(/^\/en\//, `/${lang}/`)}</em>
        </div>

        {live.pills.length > 0 && (
          <div className="lp-page__rail" aria-hidden="true">
            {live.pills.map((pill) => (
              <span key={pill.name} className={`lp-page__pill${pill.active ? " is-active" : ""}`}>
                {pill.name}
              </span>
            ))}
          </div>
        )}

        <div className="lp-page__body">
          <aside className="lp-page__side">
            <div className="lp-page__filters" aria-hidden="true">
              <p className="lp-page__filters-title">{words.filterBy}</p>
              {words.filters.map((label) => (
                <p key={label} className="lp-page__filter">
                  {label}
                  <span className="lp-page__chevron" />
                </p>
              ))}
            </div>
            <div className="lp-page__copybox">
              <p className="lp-page__copyname">{page.name}</p>
              {empty ? (
                <p className="lp-page__empty">{t("No opening text yet: the page shows its products alone.")}</p>
              ) : (
                // The writer's own text, cleaned again by the backend on save.
                <div className="lp-page__copy" dangerouslySetInnerHTML={{ __html: page.html }} />
              )}
            </div>
          </aside>

          <div className="lp-page__main">
            <div className="lp-page__headrow">
              <h3 className="lp-page__h1">{page.heading}</h3>
              <span className="lp-page__sort" aria-hidden="true">
                {words.sort}
              </span>
            </div>
            <div className="lp-page__grid">
              {cards.map((card) => (
                <div key={card.id} className={`lp-page__card${card.placeholder ? " is-placeholder" : ""}`}>
                  <div className="lp-page__media">
                    {card.was ? (
                      <span className="lp-page__save">
                        {words.save} {money(card.was - card.price)}
                      </span>
                    ) : null}
                    {card.soldOut ? <span className="lp-page__soldout">{words.soldOut}</span> : null}
                    {card.image ? <img src={card.image} alt="" /> : null}
                  </div>
                  {card.placeholder ? (
                    <>
                      <span className="lp-page__bar lp-page__bar--short" />
                      <span className="lp-page__bar" />
                      <span className="lp-page__bar lp-page__bar--price" />
                    </>
                  ) : (
                    <>
                      <p className="lp-page__brand">{card.brand}</p>
                      <p className="lp-page__title">{card.title}</p>
                      <p className="lp-page__price">
                        {card.was ? <s>{money(card.was)}</s> : null}
                        <strong>
                          <small>{words.currency}</small> {money(card.price)}
                        </strong>
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* The score checks for the page's name — the word shoppers search — not its longer heading. */}
      {!empty && <CopyChecklist items={checklist(page.html, { name: page.name, lang })} />}
    </div>
  );
}
